/**
 * Query filter helpers.
 *
 * Shared logic for applying filters to Knex queries.
 * Used by both KnexAdapter and BaseService for DRY.
 */

import type { Knex } from 'knex'
import type { FilterOperators } from '@gzl10/nexus-sdk'

/**
 * Escape LIKE wildcard characters (%, _, \) in user input.
 * Prevents wildcard injection in $contains, $startswith, $endswith operators.
 */
export function escapeLikeWildcards(value: string): string {
  return value.replace(/[%_\\]/g, '\\$&')
}

/**
 * Apply filters to a Knex query builder.
 *
 * Supports:
 * - Simple equality: `{ name: 'John' }`
 * - Operators: `{ age: { $gte: 18 } }`
 * - Array shorthand for $in: `{ status: ['active', 'pending'] }`
 * - Null check: `{ deletedAt: null }`
 *
 * @example
 * let qb = db('users')
 * qb = applyFilters(qb, { status: 'active', age: { $gte: 18 } })
 */
export function applyFilters(qb: Knex.QueryBuilder, filters: Record<string, unknown>): Knex.QueryBuilder {
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined) continue

    // $or: OR logic between filter groups
    if (key === '$or') {
      if (Array.isArray(value) && value.length > 0) {
        qb.where(function () {
          for (const group of value as Record<string, unknown>[]) {
            this.orWhere(function () {
              applyFilters(this, group)
            })
          }
        })
      }
      continue
    }

    // Null value - direct comparison
    if (value === null) {
      qb.whereNull(key)
      continue
    }

    // Empty string - skip
    if (value === '') continue

    // Array shorthand for $in
    if (Array.isArray(value) && value.length > 0) {
      qb.whereIn(key, value as (string | number)[])
      continue
    }

    // Object with operators
    if (typeof value === 'object' && !Array.isArray(value)) {
      applyFilterOperators(qb, key, value as FilterOperators)
      continue
    }

    // Simple equality
    qb.where(key, value)
  }
  return qb
}

/**
 * Apply filter operators for a single field.
 *
 * Supported operators:
 * - $eq: Equal
 * - $ne: Not equal
 * - $gt: Greater than
 * - $gte: Greater than or equal
 * - $lt: Less than
 * - $lte: Less than or equal
 * - $contains: LIKE %val%
 * - $startswith: LIKE val%
 * - $endswith: LIKE %val
 * - $in: IN (array)
 * - $nin: NOT IN (array)
 * - $isnull: IS NULL / IS NOT NULL
 */
export function applyFilterOperators(qb: Knex.QueryBuilder, field: string, operators: FilterOperators): void {
  for (const [op, val] of Object.entries(operators)) {
    if (val === undefined) continue

    switch (op) {
      case '$eq':
        if (val === null) qb.whereNull(field)
        else qb.where(field, val)
        break

      case '$ne':
        if (val === null) qb.whereNotNull(field)
        else qb.whereNot(field, val)
        break

      case '$gt':
        qb.where(field, '>', val)
        break

      case '$gte':
        qb.where(field, '>=', val)
        break

      case '$lt':
        qb.where(field, '<', val)
        break

      case '$lte':
        qb.where(field, '<=', val)
        break

      case '$contains':
        qb.where(field, 'like', `%${escapeLikeWildcards(String(val))}%`)
        break

      case '$startswith':
        qb.where(field, 'like', `${escapeLikeWildcards(String(val))}%`)
        break

      case '$endswith':
        qb.where(field, 'like', `%${escapeLikeWildcards(String(val))}`)
        break

      case '$in':
        if (Array.isArray(val) && val.length > 0) {
          qb.whereIn(field, val as (string | number)[])
        }
        break

      case '$nin':
        if (Array.isArray(val) && val.length > 0) {
          qb.whereNotIn(field, val as (string | number)[])
        }
        break

      case '$isnull':
        if (val === true) qb.whereNull(field)
        else if (val === false) qb.whereNotNull(field)
        break

      case '$between':
        if (Array.isArray(val) && val.length === 2) {
          qb.whereBetween(field, val as [string | number | Date, string | number | Date])
        }
        break
    }
  }
}

// ============================================================================
// In-Memory Filter Helpers
// ============================================================================

/**
 * Apply filters in memory to an array of items.
 *
 * Supports same operators as Knex version:
 * $eq, $ne, $gt, $gte, $lt, $lte, $contains, $startswith, $endswith, $in, $nin, $isnull
 *
 * @example
 * const filtered = applyInMemoryFilters(users, { status: 'active', age: { $gte: 18 } })
 */
export function applyInMemoryFilters<T = unknown>(items: T[], filters: Record<string, unknown>): T[] {
  return items.filter(item => {
    const record = item as Record<string, unknown>
    return matchRecordFilters(record, filters)
  })
}

/**
 * Check if a single record matches all filters (AND logic).
 * Extracted for reuse by $or groups.
 */
function matchRecordFilters(record: Record<string, unknown>, filters: Record<string, unknown>): boolean {
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined) continue

    // $or: at least one group must match
    if (key === '$or') {
      if (Array.isArray(value) && value.length > 0) {
        const orMatch = (value as Record<string, unknown>[]).some(group =>
          matchRecordFilters(record, group)
        )
        if (!orMatch) return false
      }
      continue
    }

    const itemValue = record[key]

    // Null value - check if field is null
    if (value === null) {
      if (itemValue !== null && itemValue !== undefined) return false
      continue
    }

    // Empty string - skip
    if (value === '') continue

    // Array shorthand for $in
    if (Array.isArray(value)) {
      // Empty array = no filter (return all)
      if (value.length === 0) continue
      if (!value.includes(itemValue)) return false
      continue
    }

    // Object with operators
    if (typeof value === 'object' && !Array.isArray(value)) {
      if (!matchFilterOperators(itemValue, value as FilterOperators)) return false
      continue
    }

    // Simple equality
    if (itemValue !== value) return false
  }

  return true
}

/**
 * Match filter operators against a single value (in-memory).
 */
export function matchFilterOperators(itemValue: unknown, operators: FilterOperators): boolean {
  for (const [op, val] of Object.entries(operators)) {
    if (val === undefined) continue

    switch (op) {
      case '$eq':
        if (val === null) {
          if (itemValue !== null && itemValue !== undefined) return false
        } else if (itemValue !== val) return false
        break

      case '$ne':
        if (val === null) {
          if (itemValue === null || itemValue === undefined) return false
        } else if (itemValue === val) return false
        break

      case '$gt':
        if (typeof itemValue !== 'number' && typeof itemValue !== 'string') return false
        if (!(itemValue > val)) return false
        break

      case '$gte':
        if (typeof itemValue !== 'number' && typeof itemValue !== 'string') return false
        if (!(itemValue >= val)) return false
        break

      case '$lt':
        if (typeof itemValue !== 'number' && typeof itemValue !== 'string') return false
        if (!(itemValue < val)) return false
        break

      case '$lte':
        if (typeof itemValue !== 'number' && typeof itemValue !== 'string') return false
        if (!(itemValue <= val)) return false
        break

      case '$contains':
        if (typeof itemValue !== 'string' || typeof val !== 'string') return false
        if (!itemValue.toLowerCase().includes(val.toLowerCase())) return false
        break

      case '$startswith':
        if (typeof itemValue !== 'string' || typeof val !== 'string') return false
        if (!itemValue.toLowerCase().startsWith(val.toLowerCase())) return false
        break

      case '$endswith':
        if (typeof itemValue !== 'string' || typeof val !== 'string') return false
        if (!itemValue.toLowerCase().endsWith(val.toLowerCase())) return false
        break

      case '$in':
        if (!Array.isArray(val) || val.length === 0) break
        if (!val.includes(itemValue)) return false
        break

      case '$nin':
        if (!Array.isArray(val) || val.length === 0) break
        if (val.includes(itemValue)) return false
        break

      case '$isnull':
        if (val === true && itemValue !== null && itemValue !== undefined) return false
        if (val === false && (itemValue === null || itemValue === undefined)) return false
        break

      case '$between':
        if (!Array.isArray(val) || val.length !== 2) break
        if (typeof itemValue !== 'number' && typeof itemValue !== 'string') return false
        if (!(itemValue >= val[0] && itemValue <= val[1])) return false
        break
    }
  }
  return true
}
